import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import type { AnswerV2 } from "../globals/types";
import { useAlert } from "../lib/Alerts";
import { isClosed, saveAnswerV2 } from "../lib/formsApi";
import { type Answer, toAnswerV2Body } from "../questionnaire/answers";

import { useRefetchAssessment } from "./useAssessmentDetail";

type Slot = { latest?: Answer; timer?: ReturnType<typeof setTimeout>; request?: Promise<void>; unsaved?: Answer };

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export const useSaveAnswersV2 = (
    assessmentId: string,
    token: string | undefined,
    saved: AnswerV2[],
    enabled: boolean
) => {
    const { alertUser } = useAlert();
    const { formatMessage } = useIntl();
    const slots = useRef(new Map<string, Slot>());
    const ids = useRef(new Map<string, string>());
    const refetchAssessment = useRefetchAssessment();
    const latest = useRef({ assessmentId, token, saved, alertUser, formatMessage, refetchAssessment });
    latest.current = { assessmentId, token, saved, alertUser, formatMessage, refetchAssessment };

    const slot = (key: string) => {
        const existing = slots.current.get(key);
        if (existing) return existing;
        const created: Slot = {};
        slots.current.set(key, created);
        return created;
    };

    const [status, setStatus] = useState<SaveStatus>("idle");
    const refresh = useCallback(() => {
        const all = [...slots.current.values()];
        if (all.some((s) => s.latest || s.timer || s.request)) setStatus("saving");
        else if (all.some((s) => s.unsaved)) setStatus("error");
        else if (all.length > 0) setStatus("saved");
    }, []);

    const fire = useCallback(
        (key: string, keepalive = false): Promise<void> | undefined => {
            const s = slots.current.get(key);
            if (!s?.latest || (s.request && !keepalive)) return s?.request;
            const answer = s.latest;
            s.latest = undefined;
            const { assessmentId, token, alertUser, formatMessage } = latest.current;
            const request = saveAnswerV2(token, assessmentId, key, toAnswerV2Body(answer), keepalive)
                .then((answer) => {
                    ids.current.set(key, answer.id);
                    s.unsaved = undefined;
                })
                .catch((err) => {
                    if (isClosed(err)) {
                        s.unsaved = undefined;
                        latest.current.refetchAssessment();
                        return;
                    }
                    s.unsaved = answer;
                    alertUser({
                        value: formatMessage({ defaultMessage: "Your answer could not be saved. Please try again." }),
                        severity: "error",
                    });
                })
                .finally(() => {
                    if (s.request === request) s.request = undefined;
                    if (!s.timer) fire(key);
                    refresh();
                });
            s.request = request;
            refresh();
            return request;
        },
        [refresh]
    );

    useEffect(() => {
        const flush = () =>
            slots.current.forEach((s, key) => {
                clearTimeout(s.timer);
                s.timer = undefined;
                fire(key, true);
            });
        window.addEventListener("pagehide", flush);
        return () => {
            window.removeEventListener("pagehide", flush);
            flush();
        };
    }, [fire]);

    const save = useCallback(
        (key: string, answer: Answer) => {
            if (!enabled) return;
            const s = slot(key);
            clearTimeout(s.timer);
            s.latest = answer;
            s.timer = setTimeout(() => {
                s.timer = undefined;
                fire(key);
            }, 1000);
            refresh();
        },
        [enabled, fire, refresh]
    );

    const answerId = useCallback(
        async (key: string, answer?: Answer): Promise<string> => {
            const known = () => ids.current.get(key) ?? latest.current.saved.find((a) => a.questionKey === key)?.id;
            if (known()) return known() as string;
            if (!answer) throw new Error("question has no saved answer");
            const s = slot(key);
            clearTimeout(s.timer);
            s.timer = undefined;
            s.latest ??= answer;
            while (!known() && (s.latest || s.request)) {
                await (fire(key) ?? s.request);
            }
            const id = known();
            if (!id) throw new Error("answer could not be saved");
            return id;
        },
        [fire]
    );

    const flush = useCallback(async (): Promise<boolean> => {
        slots.current.forEach((s) => {
            if (s.unsaved) s.latest ??= s.unsaved;
        });
        const pending = () => [...slots.current.entries()].filter(([, s]) => s.latest || s.timer || s.request);
        while (pending().length > 0) {
            await Promise.all(
                pending().map(([key, s]) => {
                    clearTimeout(s.timer);
                    s.timer = undefined;
                    return fire(key) ?? s.request;
                })
            );
        }
        return ![...slots.current.values()].some((s) => s.unsaved);
    }, [fire]);

    return { save, answerId, flush, status };
};
